import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  DefaultSidebar,
  Sidebar,
  WHITEBOARDS_SIDEBAR_TAB,
  CANVAS_SEARCH_TAB,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { calculateScrollCenter } from "@excalidraw/excalidraw/scene";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
  WhiteboardsIcon,
  loginIcon,
  ImageIcon,
  TrashIcon,
  checkIcon,
  saveAs,
  downloadIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import { restore, restoreAppState } from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useSetAtom,
  useAtomWithInitialValue,
  appJotaiStore,
  currentProfileAtom,
  currentWhiteboardAtom,
  saveStateAtom,
  whiteboardsLoadingAtom,
  whiteboardSwitchingAtom,
  whiteboardsAtom,
  lastSavedStateAtom,
  cancelSaveAtom,
  localBackupEnabledAtom,
  localBackupFolderHandleAtom,
  whiteboardFoldersAtom,
} from "./app-jotai";
import { ProfileSelector } from "./components/ProfileSelector/ProfileSelector";
import { WhiteboardsMenuContainer } from "./components/WhiteboardsMenuContainer";
import {
  saveWhiteboard,
  loadWhiteboards,
  updateWhiteboardLastOpened,
  updateProfileLocalBackup,
  loadWhiteboardFolders,
} from "./data/firestore";
import { whiteboardCache } from "./data/whiteboardCache";
import {
  generateWhiteboardThumbnail,
  uploadWhiteboardThumbnail,
  saveWhiteboardLocally,
  restoreBackupFolderHandle,
  removeBackupFolderHandle,
} from "./data/storage";
import { updateWhiteboardThumbnail } from "./data/firestore";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";

import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { SavingIndicator } from "./components/SavingIndicator";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import { WhiteboardsLoadingOverlay } from "./components/WhiteboardsLoadingOverlay";

import {
  exportToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import type { Profile } from "./types/profile";
import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  // Profile management
  const [currentProfile, setCurrentProfile] = useAtom(currentProfileAtom);
  const [currentWhiteboard, setCurrentWhiteboard] = useAtom(
    currentWhiteboardAtom,
  );
  const setWhiteboards = useSetAtom(whiteboardsAtom);
  const whiteboards = useAtomValue(whiteboardsAtom);
  const [saveState, setSaveState] = useAtom(saveStateAtom);
  const setWhiteboardsLoading = useSetAtom(whiteboardsLoadingAtom);
  const [lastSavedState, setLastSavedState] = useAtom(lastSavedStateAtom);
  const setCancelSave = useSetAtom(cancelSaveAtom);
  const localBackupEnabled = useAtomValue(localBackupEnabledAtom);
  const setLocalBackupEnabled = useSetAtom(localBackupEnabledAtom);
  const localBackupFolderHandle = useAtomValue(localBackupFolderHandleAtom);
  const setLocalBackupFolderHandle = useSetAtom(localBackupFolderHandleAtom);
  const setFolders = useSetAtom(whiteboardFoldersAtom);

  // Track save count for thumbnail generation
  const saveCountRef = useRef(0);
  // Track pending save to prevent Firebase queue exhaustion
  const pendingSaveRef = useRef<{
    whiteboardId: string;
    elements: readonly OrderedExcalidrawElement[];
    files: BinaryFiles;
    timeoutId: NodeJS.Timeout;
  } | null>(null);
  const activeSaveRef = useRef<Promise<void> | null>(null);

  // Clear canvas when no profile is selected
  useEffect(() => {
    if (!currentProfile && excalidrawAPI) {
      excalidrawAPI.updateScene({
        elements: [],
      });
      excalidrawAPI.resetScene();
      setLastSavedState(null);
      saveCountRef.current = 0;
      setSaveState("saved");

      // Cancel any pending saves
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current.timeoutId);
        pendingSaveRef.current = null;
      }
    }
  }, [currentProfile, excalidrawAPI, setSaveState, setLastSavedState]);

  const handleProfileUnlocked = async (profile: Profile) => {
    // Clear content cache when switching profiles
    whiteboardCache.clear();

    setCurrentProfile(profile);

    // Restore local backup setting and folder handle
    if (profile.localBackupEnabled) {
      const handle = await restoreBackupFolderHandle(profile.id);
      if (handle) {
        setLocalBackupEnabled(true);
        setLocalBackupFolderHandle(handle);
        // Permission will be verified when actually needed (during save)
      } else {
        setLocalBackupEnabled(false);
        setLocalBackupFolderHandle(null);
      }
    } else {
      setLocalBackupEnabled(false);
      setLocalBackupFolderHandle(null);
    }

    // Show loading overlay and load whiteboards immediately
    if (excalidrawAPI) {
      setWhiteboardsLoading(true);
      setWhiteboards([]);
      setCurrentWhiteboard(null);

      try {
        const [wbs, flds] = await Promise.all([
          loadWhiteboards(profile.id),
          loadWhiteboardFolders(profile.id),
        ]);
        setWhiteboards(wbs);
        setFolders(flds);

        if (wbs.length > 0) {
          // Find the most recently used whiteboard
          const mostRecent = wbs.reduce((latest, wb) =>
            wb.lastOpened > latest.lastOpened ? wb : latest,
          );

          // Load content for the most recent whiteboard only (with caching)
          const content = await whiteboardCache.get(mostRecent.id);
          const loadedWhiteboard = {
            ...mostRecent,
            elements: content.elements,
            files: content.files,
          };

          setCurrentWhiteboard(loadedWhiteboard);

          // Update the whiteboards array cache with loaded content
          setWhiteboards(
            wbs.map((wb) => (wb.id === mostRecent.id ? loadedWhiteboard : wb)),
          );

          await updateWhiteboardLastOpened(mostRecent.id);

          // Clear lastSavedState BEFORE updateScene to prevent race condition
          // This ensures onChange will initialize it from loaded elements
          setLastSavedState(null);

          // Load the whiteboard content into the canvas
          excalidrawAPI.updateScene({
            elements: content.elements || [],
            captureUpdate: CaptureUpdateAction.NEVER,
          });

          if (content.files && Object.keys(content.files).length > 0) {
            excalidrawAPI.addFiles(
              Object.entries(content.files).map(([id, file]) => ({
                ...file,
                id: id as any,
              })),
            );
          }

          // Clear undo/redo history
          excalidrawAPI.history.clear();

          // Apply scroll center after elements are loaded
          const scrollCenter = calculateScrollCenter(
            content.elements || [],
            excalidrawAPI.getAppState(),
          );
          excalidrawAPI.updateScene({
            appState: scrollCenter,
          });

          // Background loading disabled - was for Recent view which is now removed
          // Uncomment below to re-enable background preloading of recent whiteboards
          /*
          const recentWhiteboards = [...wbs]
            .sort((a, b) => b.lastOpened - a.lastOpened)
            .slice(0, 10);

          const backgroundLoads = recentWhiteboards
            .slice(1)
            .map(async (wb, index) => {
              try {
                await new Promise((resolve) =>
                  setTimeout(resolve, index * 300),
                );
                await whiteboardCache.get(wb.id);
                console.log(`[Background] Cached whiteboard: ${wb.name}`);
              } catch (err) {
                console.warn(`[Background] Failed to load ${wb.name}:`, err);
              }
            });

          Promise.all(backgroundLoads).then(() => {
            console.log("[Background] Completed loading recent whiteboards");
          });
          */
        }
      } finally {
        setWhiteboardsLoading(false);
      }
    }
  };

  // Check if there are unsaved changes (lightweight check)
  const hasUnsavedChanges = useCallback(
    (elements: readonly OrderedExcalidrawElement[], files: BinaryFiles) => {
      if (!lastSavedState) {
        return false;
      }

      // Quick checks first (avoid expensive operations)
      if (elements.length !== lastSavedState.elementsLength) {
        return true;
      }
      if (Object.keys(files).length !== lastSavedState.filesCount) {
        return true;
      }

      // Check element versions (much lighter than full stringify)
      // Each element has a version that increments on changes
      const currentVersionSum = elements.reduce(
        (sum, el) => sum + (el.version || 0),
        0,
      );

      if (currentVersionSum !== lastSavedState.versionSum) {
        return true;
      }

      return false;
    },
    [lastSavedState],
  );

  // Save function that queues only the latest save and waits for active save to complete
  const queueSave = useCallback(
    (
      whiteboardId: string,
      elements: readonly OrderedExcalidrawElement[],
      files: BinaryFiles,
    ) => {
      // Cancel any existing pending save
      if (pendingSaveRef.current) {
        clearTimeout(pendingSaveRef.current.timeoutId);
      }

      // Queue the new save
      const timeoutId = setTimeout(async () => {
        // Wait for any active save to complete first
        if (activeSaveRef.current) {
          await activeSaveRef.current;
        }

        // Check if this save was superseded by another pending save
        if (
          pendingSaveRef.current?.whiteboardId !== whiteboardId ||
          pendingSaveRef.current?.timeoutId !== timeoutId
        ) {
          return; // This save was cancelled
        }

        // Execute the save
        const savePromise = (async () => {
          try {
            setSaveState("saving");
            const didSave = await saveWhiteboard(
              whiteboardId,
              [...elements],
              files,
            );

            // Only update state if we actually saved (not just queued as pending)
            if (!didSave) {
              return;
            }

            // Local backup if enabled
            if (localBackupEnabled && localBackupFolderHandle) {
              const wb = whiteboards.find((w) => w.id === whiteboardId);
              if (wb) {
                saveWhiteboardLocally(
                  localBackupFolderHandle,
                  wb.name,
                  [...elements],
                  files,
                ).catch((err) => {
                  // eslint-disable-next-line no-console
                  console.warn("Local backup failed:", err);
                });
              }
            }

            // Increment save counter
            saveCountRef.current += 1;

            // Generate and upload thumbnail every 10 saves (non-blocking)
            if (
              excalidrawAPI &&
              elements.length > 0 &&
              saveCountRef.current % 10 === 0
            ) {
              (async () => {
                try {
                  const blob = await generateWhiteboardThumbnail(excalidrawAPI);
                  const url = await uploadWhiteboardThumbnail(
                    whiteboardId,
                    blob,
                  );
                  await updateWhiteboardThumbnail(whiteboardId, url);
                  console.log("Thumbnail generated:", whiteboardId); // eslint-disable-line no-console
                } catch (thumbnailError) {
                  console.warn("Failed to generate thumbnail:", thumbnailError); // eslint-disable-line no-console
                }
              })();
            }

            // Update last saved state with version sum
            const versionSum = elements.reduce(
              (sum, el) => sum + (el.version || 0),
              0,
            );
            setLastSavedState({
              elementsLength: elements.length,
              filesCount: Object.keys(files).length,
              versionSum,
            });

            // Update cache
            if (whiteboards.some((wb) => wb.id === whiteboardId)) {
              setWhiteboards(
                whiteboards.map((wb) =>
                  wb.id === whiteboardId
                    ? { ...wb, elements: [...elements], files }
                    : wb,
                ),
              );
            }

            // Update content cache with saved data
            whiteboardCache.update(whiteboardId, [...elements], files);

            setSaveState("saved");
          } catch (error) {
            setSaveState("unsaved");
            console.error("Failed to save whiteboard:", error); // eslint-disable-line no-console
          } finally {
            activeSaveRef.current = null;
            if (pendingSaveRef.current?.whiteboardId === whiteboardId) {
              pendingSaveRef.current = null;
            }
          }
        })();

        activeSaveRef.current = savePromise;
      }, 2000); // 2 second delay

      pendingSaveRef.current = { whiteboardId, elements, files, timeoutId };
    },
    [
      excalidrawAPI,
      whiteboards,
      setLastSavedState,
      setWhiteboards,
      setSaveState,
      localBackupEnabled,
      localBackupFolderHandle,
    ],
  );

  // Cancel function for switching whiteboards
  const cancelPendingSave = useCallback(() => {
    if (pendingSaveRef.current) {
      clearTimeout(pendingSaveRef.current.timeoutId);
      pendingSaveRef.current = null;
    }
  }, []);

  // Store cancel function in atom
  useEffect(() => {
    setCancelSave(() => cancelPendingSave);
  }, [setCancelSave, cancelPendingSave]);

  // Ctrl+S handler for manual local save - always prompts for location
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        e.stopPropagation(); // Prevent Excalidraw's built-in Ctrl+S action

        if (!currentWhiteboard || !excalidrawAPI) {
          return;
        }

        try {
          const elements = excalidrawAPI.getSceneElements();
          const files = excalidrawAPI.getFiles();

          // Create proper Excalidraw file format
          const excalidrawData = {
            type: "excalidraw",
            version: 2,
            source: "excalidraw-local",
            elements: [...elements],
            appState: {
              viewBackgroundColor: "#ffffff",
              gridSize: null,
            },
            files,
          };

          const data = JSON.stringify(excalidrawData, null, 2);
          const blob = new Blob([data], { type: "application/json" });

          // Always prompt for save location - add timestamp to force new picker dialog
          const timestamp = new Date()
            .toISOString()
            .replace(/[:.]/g, "-")
            .slice(0, -5);
          // @ts-ignore - showSaveFilePicker is not in all TS libs
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: `${currentWhiteboard.name}_${timestamp}.excalidraw`,
            types: [
              {
                description: "Excalidraw files",
                accept: { "application/json": [".excalidraw"] },
              },
            ],
            excludeAcceptAllOption: true,
          });

          const writable = await (fileHandle as any).createWritable();
          await writable.write(blob);
          await writable.close();

          // eslint-disable-next-line no-console
          console.log("[Ctrl+S] Saved to file");
        } catch (err: any) {
          // User cancelled is not an error
          if (err?.name !== "AbortError") {
            console.error("[Ctrl+S] Failed to save:", err);
          }
        }
      }
    };

    // Capture phase to intercept before Excalidraw's handlers
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [currentWhiteboard, excalidrawAPI]);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // Initialize lastSavedState on first onChange if not set (from initial load)
    if (currentWhiteboard?.id && !lastSavedState) {
      const versionSum = elements.reduce(
        (sum, el) => sum + (el.version || 0),
        0,
      );
      setLastSavedState({
        elementsLength: elements.length,
        filesCount: Object.keys(files).length,
        versionSum,
      });
      setSaveState("saved");
      return; // Don't save on initial load
    }

    // Check for unsaved changes and update state
    if (currentWhiteboard?.id) {
      if (hasUnsavedChanges(elements, files)) {
        // Only queue a new save if we're not currently saving
        if (saveState !== "saving") {
          setSaveState("unsaved");
        }
        queueSave(currentWhiteboard.id, elements, files);
      } else if (lastSavedState) {
        // No changes, ensure state is saved
        setSaveState("saved");
      }
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        window.devicePixelRatio,
        () => forceRefresh((prev) => !prev),
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile, appState) => {
          if (isMobile) {
            return null;
          }

          return (
            <div className="top-right-ui">
              <SavingIndicator />
              {collabError.message && <CollabError collabError={collabError} />}
              {!isCollabDisabled && collabAPI && (
                <>
                  {appState.openSidebar?.tab !== CANVAS_SEARCH_TAB && (
                    <LiveCollaborationTrigger
                      isCollaborating={isCollaborating}
                      onSelect={() =>
                        setShareDialogState({ isOpen: true, type: "share" })
                      }
                    />
                  )}
                </>
              )}
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
            ...(currentProfile
              ? [
                  {
                    label: "Change Password",
                    category: DEFAULT_CATEGORIES.app,
                    icon: loginIcon,
                    keywords: ["password", "security", "profile", "settings"],
                    perform: () => {
                      // This will be handled via a window event
                      window.dispatchEvent(
                        new CustomEvent("excalidraw:change-password"),
                      );
                    },
                  },
                  {
                    label: currentProfile.pictureUrl
                      ? "Change Profile Picture"
                      : "Upload Profile Picture",
                    category: DEFAULT_CATEGORIES.app,
                    icon: ImageIcon,
                    keywords: [
                      "picture",
                      "photo",
                      "avatar",
                      "profile",
                      "settings",
                      "image",
                    ],
                    perform: () => {
                      document.getElementById("profile-picture-input")?.click();
                    },
                  },
                  ...(currentProfile.pictureUrl
                    ? [
                        {
                          label: "Remove Profile Picture",
                          category: DEFAULT_CATEGORIES.app,
                          icon: TrashIcon,
                          keywords: [
                            "picture",
                            "photo",
                            "avatar",
                            "profile",
                            "delete",
                            "remove",
                          ],
                          perform: () => {
                            window.dispatchEvent(
                              new CustomEvent("excalidraw:remove-picture"),
                            );
                          },
                        },
                      ]
                    : []),
                  {
                    label: localBackupEnabled
                      ? "Disable Local Backup"
                      : "Enable Local Backup",
                    category: DEFAULT_CATEGORIES.app,
                    icon: checkIcon,
                    keywords: [
                      "backup",
                      "local",
                      "save",
                      "folder",
                      "file",
                      "export",
                    ],
                    perform: () => {
                      window.dispatchEvent(
                        new CustomEvent("excalidraw:toggle-backup"),
                      );
                    },
                  },
                  ...(localBackupEnabled
                    ? [
                        {
                          label: "Change Backup Folder",
                          category: DEFAULT_CATEGORIES.app,
                          icon: saveAs,
                          keywords: [
                            "backup",
                            "folder",
                            "directory",
                            "location",
                            "path",
                          ],
                          perform: () => {
                            window.dispatchEvent(
                              new CustomEvent(
                                "excalidraw:change-backup-folder",
                              ),
                            );
                          },
                        },
                        {
                          label: `Backup All Whiteboards (${whiteboards.length})`,
                          category: DEFAULT_CATEGORIES.export,
                          icon: downloadIcon,
                          keywords: [
                            "backup",
                            "all",
                            "download",
                            "save",
                            "export",
                            "whiteboards",
                          ],
                          perform: () => {
                            window.dispatchEvent(
                              new CustomEvent("excalidraw:backup-all"),
                            );
                          },
                        },
                      ]
                    : []),
                ]
              : []),
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
        <DefaultSidebar.TabTriggers>
          <Sidebar.TabTrigger tab={WHITEBOARDS_SIDEBAR_TAB}>
            {WhiteboardsIcon}
          </Sidebar.TabTrigger>
        </DefaultSidebar.TabTriggers>
        <DefaultSidebar>
          <Sidebar.Tab tab={WHITEBOARDS_SIDEBAR_TAB}>
            <WhiteboardsMenuContainer excalidrawAPI={excalidrawAPI} />
          </Sidebar.Tab>
        </DefaultSidebar>
      </Excalidraw>

      {/* Whiteboards Loading Overlay */}
      <WhiteboardsLoadingOverlay />

      {/* Profile Selector - Show if no profile is selected */}
      {!currentProfile && (
        <ProfileSelector onProfileUnlocked={handleProfileUnlocked} />
      )}
    </div>
  );
};

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
